import { t } from "@lingui/macro";
import {
  BankAccountConverterDocument,
  type BankAccountConverterQuery,
  type BankAccountConverterQueryVariables,
} from "@src/__generated__/graphql";
import type {
  CompanyBankAccount,
  CompanyData,
} from "@src/__generated__/urql-graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { action, computed, makeObservable, observable } from "mobx";
import { Form } from "../form";

export class Store {
  @observable
  form: Form;

  @observable open = false;

  private bankAccountConverterFetcher = new FetchHelper<
    BankAccountConverterQuery,
    BankAccountConverterQueryVariables
  >(BankAccountConverterDocument);

  constructor() {
    makeObservable(this);
    this.form = new Form();
  }

  @action setCompanyData(
    companyData: CompanyData,
    options?: { bankAccounts: CompanyBankAccount[] },
  ) {
    this.form.fill({ ...companyData, country_id: companyData.country?.id });
    this.form.bankAccounts = options?.bankAccounts || [];
  }

  @action.bound toggleOpen(open?: boolean) {
    this.open = open ?? !this.open;
  }

  @computed
  get isCzechContractor() {
    const country = this.form.countryList.find(
      (country) => country.id === this.form.country_id.value,
    );
    return country && country.code === "CZ";
  }

  @computed
  get isConvertingBankAccountToIban() {
    return this.bankAccountConverterFetcher.isLoading.value;
  }

  @computed
  get canConvertBankAccountToIban() {
    return (
      this.form.bank_account.value.length > 0 &&
      this.form.bank_account_code.value.length > 0
    );
  }

  @action.bound
  async convertBankAccountToIban() {
    if (!this.canConvertBankAccountToIban) {
      return;
    }

    const [data, error] = await this.bankAccountConverterFetcher.fetch({
      country_id: this.form.country_id.value,
      account_number: this.form.bank_account.value,
      account_code: this.form.bank_account_code.value,
    });

    if (error || !data || !data.bankAccountConverter.iban) {
      appStore.UIStore.toast({
        status: "error",
        description: t`Unable to convert bank account to IBAN.`,
      });
      return;
    }

    this.form.iban.onChange(data.bankAccountConverter.iban);
  }
}
